import type {
  CookieConsentConfig,
  CookieConsentProductResult,
  CookiePolicyType,
} from '../types/cookieConsent';
import { isRunningInEvoIframe } from './iframeDetection';

export { isRunningInEvoIframe } from './iframeDetection';

const COOKIE_CONSENT_CONFIG: CookieConsentConfig = {
  policies: ['essential', 'analytics', 'functional'],
  parties: [
    {
      policy: 'analytics',
      vendors: [
        {
          name: 'Wootric',
          key: 'wootric',
          description: 'Net Promoter Score surveys to collect user feedback and improve our product',
          vendorUrl: 'https://www.wootric.com/privacy-policy/',
          partyType: 'Third Party',
        },
      ],
    },
  ],
  applicationId: 'd17bc834-4b46-4604-b450-e80f70985241',
  policyVersion: '2025-01',
  policyUrl: 'https://www.theaccessgroup.com/en-gb/privacy-policy/',
};

/**
 * Check if a specific policy type has been consented to.
 *
 * Consent can be:
 * - `'*'` → user accepted all cookies
 * - `null` → user accepted essential only
 * - `CookieConsentDetailedEntry[]` → granular consent per policy
 */
export function hasPolicyConsent(
  consent: CookieConsentProductResult | null,
  policyType: CookiePolicyType,
): boolean {
  if (!consent) {
    return false;
  }

  // '*' means the user accepted all cookies
  if (consent.consent === '*') {
    return true;
  }

  // null means the user accepted essential only
  if (consent.consent === null) {
    return policyType === 'essential';
  }

  // Detailed consent: check if the policy type is in the array
  return consent.consent.some(
    (entry) => entry.policy === policyType,
  );
}

/**
 * Check whether the Cookie Policies SDK script has been loaded.
 */
export function isSdkAvailable(): boolean {
  return typeof window.AccessCookiePolicies?.SDK?.tabInitialise === 'function';
}

/**
 * Build an essential-only consent result.
 * Used as a fallback when the SDK is not available.
 */
function buildEssentialOnlyConsent(): CookieConsentProductResult {
  return {
    pid: COOKIE_CONSENT_CONFIG.applicationId,
    consent: null,
    policyVersion: COOKIE_CONSENT_CONFIG.policyVersion,
  };
}

/**
 * Remove the SDK's cookie-consent modal from the DOM.
 * The SDK renders a container with `id="access-cookie-policies-container"`.
 * Used as emergency cleanup when the SDK throws an error.
 */
function removeSdkModal(): void {
  const container = document.getElementById('access-cookie-policies-container');
  if (container) {
    container.remove();
  }
  // Restore body styles the SDK may have set
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
  // Remove inert attributes the SDK adds to body children
  Array.from(document.body.children).forEach((child) => {
    if (child.hasAttribute('inert')) {
      child.removeAttribute('inert');
    }
  });
}

/**
 * Initialise cookie consent using the Access Cookie Policies SDK.
 * Returns the consent result for this product, or null if the SDK is
 * not available or the app is not running inside an Evo iframe.
 *
 * The SDK's `tabInitialise` discovers the parent Evo environment
 * automatically via `postMessage` and communicates consent through the
 * Evo shell, which stores it server-side. We intentionally do NOT pass
 * an explicit `environment` parameter so the SDK can resolve the correct
 * Evo parent origin on its own.
 */
export async function initialiseCookieConsent(): Promise<CookieConsentProductResult | null> {
  if (!isSdkAvailable()) {
    return null;
  }

  if (!isRunningInEvoIframe()) {
    return null;
  }

  try {
    const consent = await window.AccessCookiePolicies!.SDK.tabInitialise({
      config: COOKIE_CONSENT_CONFIG,
    });

    return consent;
  } catch {
    removeSdkModal();
    return buildEssentialOnlyConsent();
  }
}

/**
 * Reopen the cookie consent preferences dialog by calling the SDK's
 * `showProductModal` method. This shows the product-specific preferences
 * UI managed by the SDK, allowing users to change their consent choices.
 *
 * Requires the SDK to have been initialised via `tabInitialise` first
 * (which sets the internal config and environment used by the modal).
 */
export function showCookiePreferences(): void {
  const sdk = window.AccessCookiePolicies?.SDK;
  if (!sdk) {
    return;
  }

  if (typeof sdk.injectCSS === 'function') {
    sdk.injectCSS();
  }

  if (typeof sdk.showProductModal === 'function') {
    sdk.showProductModal();
  }
}

export const cookieConsentConfig = COOKIE_CONSENT_CONFIG;
